import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { downloadAttachment } from '../lib/download';
import { dateTime, paymentClaimLabels, paymentClaimTone } from '../lib/format';
import { haptic } from '../lib/telegram';
import type { Order, PaymentClaim } from '../types';
import { Button, Card, ErrorNotice, Field, Input, Modal } from './ui';

type Dialog = { kind: 'edit' | 'reject'; claim: PaymentClaim } | null;

/**
 * Сообщения заказчика об оплате. Приложение перевод не видит — видит его
 * художник в своём банке, поэтому решение всегда за ним: подтвердить как есть,
 * исправить сумму или отказать. Пока он не решил, деньги в счёт не идут.
 */
export function PaymentClaims({ order }: { order: Order }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const resolve = useMutation({
    mutationFn: ({
      claim,
      action,
      body,
    }: {
      claim: PaymentClaim;
      action: 'confirm' | 'reject';
      body: Record<string, unknown>;
    }) => api(`/orders/${order.id}/payment-claims/${claim.id}/${action}`, { method: 'POST', body }),
    onSuccess: () => {
      haptic('success');
      close();
      void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const close = () => {
    setDialog(null);
    setAmount('');
    setNote('');
  };

  const pending = order.paymentClaims.filter((claim) => claim.status === 'PENDING');
  const resolved = order.paymentClaims.filter((claim) => claim.status !== 'PENDING');

  if (order.paymentClaims.length === 0) {
    return null;
  }

  return (
    <>
      <div className="section-heading">
        <h2>Сообщения об оплате</h2>
        {pending.length > 0 && <span className="meta">{pending.length} ждёт</span>}
      </div>

      <Card className="stack stack--compact">
        {pending.length === 0 && <p className="muted">Новых сообщений нет.</p>}

        {pending.map((claim) => (
          <div key={claim.id} className="claim">
            <div className="card-row">
              <strong>
                {claim.amount} {claim.currency}
              </strong>
              <span className="meta">{dateTime(claim.createdAt)}</span>
            </div>
            {claim.method && <p className="meta">Чем платил: {claim.method}</p>}
            {claim.comment && <p>{claim.comment}</p>}
            {claim.receipt?.downloadUrl && (
              <button
                type="button"
                className="link-button"
                onClick={() =>
                  void downloadAttachment(claim.receipt!.downloadUrl!, claim.receipt!.fileName)
                }
              >
                Открыть чек: {claim.receipt.fileName}
              </button>
            )}

            <p className="muted settings-note">
              Подтверждённая сумма сразу попадёт в журнал платежей.
            </p>
            <div className="claim__actions">
              <Button
                disabled={resolve.isPending}
                onClick={() => resolve.mutate({ claim, action: 'confirm', body: {} })}
              >
                Подтвердить
              </Button>
              <Button
                variant="secondary"
                disabled={resolve.isPending}
                onClick={() => {
                  setDialog({ kind: 'edit', claim });
                  setAmount(claim.amount);
                }}
              >
                Исправить сумму
              </Button>
              <Button
                variant="secondary"
                disabled={resolve.isPending}
                onClick={() => setDialog({ kind: 'reject', claim })}
              >
                Отказать
              </Button>
            </div>
          </div>
        ))}

        {resolve.error && <ErrorNotice error={resolve.error} />}

        {/* Разобранные остаются в списке: заказчик видит те же строки, и спор
            «я же присылал» решается историей, а не памятью. */}
        {resolved.map((claim) => (
          <div key={claim.id} className="card-row claim claim--resolved">
            <div>
              <strong>
                {claim.confirmedAmount ?? claim.amount} {claim.currency}
              </strong>
              {claim.confirmedAmount && claim.confirmedAmount !== claim.amount && (
                <p className="meta">Заказчик называл {claim.amount}</p>
              )}
              {claim.resolutionNote && <p className="meta">{claim.resolutionNote}</p>}
            </div>
            <span className={`meta ${paymentClaimTone(claim.status)}`}>
              {paymentClaimLabels[claim.status]}
            </span>
          </div>
        ))}
      </Card>

      {dialog && (
        <Modal title={dialog.kind === 'edit' ? 'Исправить сумму' : 'Отказать'} onClose={close}>
          <form
            // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
            noValidate
            className="stack stack--compact"
            onSubmit={(event) => {
              event.preventDefault();
              resolve.mutate(
                dialog.kind === 'edit'
                  ? {
                      claim: dialog.claim,
                      action: 'confirm',
                      body: { amount: amount.trim(), comment: note.trim() },
                    }
                  : { claim: dialog.claim, action: 'reject', body: { comment: note.trim() } },
              );
            }}
          >
            {dialog.kind === 'edit' && (
              <Field
                label="Сколько пришло на самом деле"
                hint={`Заказчик написал ${dialog.claim.amount} ${dialog.claim.currency}`}
              >
                <Input
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </Field>
            )}
            {/* Причина обязательна в обоих окнах: заказчик отправлял одно число и
              должен понимать, что случилось, а не гадать. */}
            <Field
              label="Что написать заказчику"
              hint={
                dialog.kind === 'edit'
                  ? 'Он увидит это вместе с исправленной суммой'
                  : 'Он увидит это вместо подтверждения'
              }
            >
              <Input
                value={note}
                placeholder={
                  dialog.kind === 'edit' ? 'Банк удержал комиссию' : 'Перевод пока не дошёл'
                }
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>
            {resolve.error && <ErrorNotice error={resolve.error} />}
            <Button
              type="submit"
              disabled={
                resolve.isPending || !note.trim() || (dialog.kind === 'edit' && !amount.trim())
              }
            >
              {resolve.isPending ? 'Сохраняем…' : dialog.kind === 'edit' ? 'Зачесть' : 'Отказать'}
            </Button>
          </form>
        </Modal>
      )}
    </>
  );
}
